/**
 * MessagingChannels — rangée de canaux de communication multi-canal.
 *
 * Layer 1 (click-to-chat) :
 *   • WhatsApp · Messenger · Instagram · SMS · Email
 *
 * Le composant interroge /api/messaging/channels pour connaître les
 * canaux activés sur la plateforme, puis génère à la demande un lien
 * pré-rempli via /api/messaging/intent.
 *
 * Props :
 *   - context : "workspace" | "audit" | "spécialiste" | "support" (defaults)
 *   - workspaceToken : si présent, le clic est loggé dans la timeline
 *   - expertId : pour le contexte "spécialiste" (numéro récupéré côté backend)
 *   - variant : "row" (par défaut) | "stack" (vertical)
 *   - title : titre optionnel
 *   - subtitle : sous-titre optionnel
 *   - testId : pour les tests
 */
import { useEffect, useState } from "react";
import {
  MessageCircle, MessagesSquare, Instagram, Smartphone, Mail, Loader2,
} from "lucide-react";
import { apiClient } from "@/lib/api";

const ICONS = {
  whatsapp: MessageCircle,
  messenger: MessagesSquare,
  instagram: Instagram,
  sms: Smartphone,
  email: Mail,
};

export default function MessagingChannels({
  context = "support",
  workspaceToken = null,
  expertId = null,
  variant = "row",
  title = null,
  subtitle = null,
  testId = "messaging-channels",
}) {
  const [channels, setChannels] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    apiClient.get("/messaging/channels")
      .then((r) => setChannels(r.data?.channels || []))
      .catch(() => setChannels([]));
  }, []);

  const openIntent = async (channelId) => {
    if (busy) return;
    setBusy(channelId);
    try {
      const r = await apiClient.post("/messaging/intent", {
        channel: channelId,
        context,
        workspace_token: workspaceToken,
        expert_id: expertId,
      });
      const url = r.data?.url;
      if (url) {
        // Ouvre dans un onglet/app dédié
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Canal indisponible.";
      const { toast } = await import("sonner");
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  if (!channels) return null;
  const enabled = channels.filter((c) => c.enabled);
  if (enabled.length === 0) return null;

  const isStack = variant === "stack";

  return (
    <section
      data-testid={testId}
      className={isStack
        ? "rounded-2xl bg-white border border-slate-100 p-4 space-y-3"
        : "rounded-2xl bg-white border border-slate-100 p-4"}
    >
      {(title || subtitle) && (
        <div className="mb-3">
          {title && (
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#8A6E3A]">
              {title}
            </p>
          )}
          {subtitle && (
            <p className="text-[12px] text-slate-600 italic mt-0.5 leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <div className={isStack ? "space-y-2" : "flex flex-wrap gap-2"}>
        {enabled.map((c) => {
          const Icon = ICONS[c.id] || MessageCircle;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => openIntent(c.id)}
              data-testid={`messaging-channel-${c.id}`}
              disabled={busy === c.id}
              className={isStack
                ? "w-full inline-flex items-center gap-3 px-3 py-2 rounded-xl border border-slate-200 hover:border-[#C9A961] bg-white text-left transition disabled:opacity-50"
                : "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#C9A961] hover:bg-[#FAF7F1] text-[12px] text-[#0B3A2A] transition disabled:opacity-50"}
              style={{ borderLeft: isStack ? `3px solid ${c.accent}` : undefined }}
            >
              {busy === c.id
                ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: c.accent }} />
                : <Icon className="w-4 h-4" style={{ color: c.accent }} />}
              <span className={isStack ? "flex-1" : ""}>
                <span className="font-medium">{c.label}</span>
                {isStack && c.tagline && (
                  <span className="block text-[11px] text-slate-500 italic">{c.tagline}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
